import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

interface Course {
  id: number;
  title: string;
  passThreshold: number;
  pageCount: number;
  lastResult: { score: number; completedAt: string } | null;
}

interface Props { auth: boolean; access: any; }

function courseEmoji(title: string): string {
  const t = title.toLowerCase();
  if (t.includes('gdpr') || t.includes('data') || t.includes('security') || t.includes('password')) return '🔒';
  if (t.includes('safe') || t.includes('safeguard')) return '🛡️';
  if (t.includes('health') || t.includes('safety') || t.includes('first aid')) return '🏥';
  if (t.includes('fire')) return '🔥';
  if (t.includes('manual') || t.includes('handling')) return '📦';
  if (t.includes('induct') || t.includes('onboard')) return '🌱';
  if (t.includes('finance') || t.includes('money')) return '💰';
  if (t.includes('customer') || t.includes('client')) return '🤝';
  return '📚';
}

function courseIconBg(pct: number): string {
  if (pct >= 100) return 'rgba(16,185,129,.1)';
  if (pct > 0)    return 'rgba(99,102,241,.1)';
  return 'rgba(239,68,68,.1)';
}

function progressBg(pct: number): string {
  if (pct >= 100) return 'linear-gradient(90deg,#10b981,#34d399)';
  if (pct > 0)    return 'linear-gradient(90deg,#6366f1,#a78bfa)';
  return 'linear-gradient(90deg,#ef4444,#f87171)';
}

function pctColor(pct: number): string {
  if (pct >= 100) return '#059669';
  if (pct > 0)    return '#6366f1';
  return '#ef4444';
}

const MyTrainingCard: React.FC<Props> = ({ auth, access }) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCourses = useCallback(async () => {
    try {
      const { data } = await axios.get('training/assigned');
      setCourses(Array.isArray(data) ? data : []);
    } catch { }
    setLoading(false);
  }, []);

  useEffect(() => { if (auth) fetchCourses(); }, [auth, fetchCourses]);

  const getPct = useCallback((c: Course): number => {
    if (!c.lastResult) return 0;
    const score = c.lastResult.score ?? 0;
    return score >= (c.passThreshold || 70) ? 100 : Math.round(score);
  }, []);

  const { completed, inProgress, notStarted } = useMemo(() =>
    courses.reduce(
      (acc, c) => {
        const p = getPct(c);
        if (p >= 100) acc.completed++;
        else if (p > 0) acc.inProgress++;
        else acc.notStarted++;
        return acc;
      },
      { completed: 0, inProgress: 0, notStarted: 0 },
    ),
  [courses, getPct]);

  const hasAccess = access?.training_view === true || access?.training_admin === true;
  if (!hasAccess) return null;

  return (
    <div className="dashboard-card" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 18 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)' }}>
          My training
        </span>
        <Link
          to="/training"
          style={{ marginLeft: 'auto', padding: '4px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'var(--surface2, var(--btn-user-form))', color: 'var(--text-color-calendar)', border: '1px solid var(--border-new, rgba(0,10,60,.08))', textDecoration: 'none' }}
        >
          All courses →
        </Link>
      </div>

      {/* Course list */}
      <div style={{ flex: 1 }}>
        {loading ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 12 }}>Loading…</div>
        ) : courses.length === 0 ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 12 }}>No courses assigned</div>
        ) : (
          courses.slice(0, 4).map(c => {
            const pct = getPct(c);
            const meta = pct >= 100
              ? `Completed ${c.lastResult ? new Date(c.lastResult.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}`
              : pct > 0 ? `In progress · ${c.pageCount} pages`
              : c.pageCount ? `${c.pageCount} page${c.pageCount !== 1 ? 's' : ''} · Not started`
              : 'Not started';

            return (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border2-new, rgba(0,10,60,.05))' }}>
                <div style={{ width: 40, height: 40, borderRadius: 11, background: courseIconBg(pct), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                  {courseEmoji(c.title)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.title}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>{meta}</div>
                  <div style={{ height: 4, borderRadius: 2, background: 'var(--border-new, rgba(0,10,60,.08))', overflow: 'hidden', marginTop: 6 }}>
                    <div style={{ height: '100%', borderRadius: 2, width: `${pct}%`, background: progressBg(pct), transition: 'width .5s' }} />
                  </div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: pctColor(pct), flexShrink: 0, minWidth: 36, textAlign: 'right' }}>
                  {pct}%
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer stats */}
      {!loading && courses.length > 0 && (
        <div style={{ display: 'flex', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border2-new, rgba(0,10,60,.05))' }}>
          {[
            { label: 'Completed',   n: completed,  color: '#059669' },
            { label: 'In progress', n: inProgress,  color: '#6366f1' },
            { label: 'Not started', n: notStarted,  color: '#ef4444' },
          ].map((s, i, arr) => (
            <React.Fragment key={s.label}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: s.color, letterSpacing: '-.03em' }}>{s.n}</div>
                <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>{s.label}</div>
              </div>
              {i < arr.length - 1 && <div style={{ width: 1, background: 'var(--border2-new, rgba(0,10,60,.05))' }} />}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyTrainingCard;
